import React, { useState } from 'react';
import { createSlide, updateSlide } from '../../services/api';
import { FormInput } from '../components/FormInput';
import { useImageUpload } from '../hooks/useImageUpload';
import { getFullImageUrl } from '../utils/helpers';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://inteliheads.in/api';

const HeroForm = ({ editingItem, onClose, onRefresh }) => {
  const [formData, setFormData] = useState({
    title: editingItem?.title || '',
    subtitle: editingItem?.subtitle || '',
    description: editingItem?.description || '',
    buttonText: editingItem?.buttonText || '',
    buttonLink: editingItem?.buttonLink || '#contact',
    imageUrl: editingItem?.imageUrl || '',
    backgroundColor: editingItem?.backgroundColor || 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(editingItem?.imageUrl ? getFullImageUrl(editingItem.imageUrl) : '');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.imageUrl;
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', imageFile);
    
    try {
      const token = localStorage.getItem('adminToken');
      
      // Try S3 product upload endpoint
      let response = await fetch(`${API_BASE}/upload/s3/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });
      
      // If S3 fails, try local upload
      if (!response.ok) {
        console.log('S3 upload failed, trying local upload...');
        response = await fetch(`${API_BASE}/upload/local/image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: uploadFormData
        });
      }
      
      const result = await response.json();
      if (result.success) {
        toast.success('Image uploaded successfully');
        return result.data.url;
      } else {
        toast.error(result.message || 'Upload failed');
        return formData.imageUrl;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading image');
      return formData.imageUrl;
    } finally {
      setUploading(false);
    }
  };

  const backgroundOptions = [
    { value: 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)', label: 'Dark Blue Gradient' },
    { value: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)', label: 'Teal Gradient' },
    { value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', label: 'Purple Gradient' },
    { value: '#FFFFFF', label: 'White' },
    { value: '#F7F9FC', label: 'Light Gray' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let imageUrl = formData.imageUrl;
    
    if (imageFile) {
      imageUrl = await uploadImage();
    }
    
    const submitData = {
      title: formData.title,
      subtitle: formData.subtitle,
      description: formData.description,
      buttonText: formData.buttonText,
      buttonLink: formData.buttonLink,
      imageUrl: imageUrl,
      backgroundColor: formData.backgroundColor,
      order: parseInt(formData.order) || 0,
      isActive: formData.isActive
    };
    
    console.log('Submitting slide data:', submitData);
    
    try {
      if (editingItem && editingItem._id) {
        await updateSlide(editingItem._id, submitData);
        toast.success('Slide updated successfully');
      } else {
        await createSlide(submitData);
        toast.success('Slide created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving slide:', error);
      toast.error(error.message || 'Error saving slide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '650px', width: '90%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Hero Slide' : 'Create Hero Slide'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <FormInput label="Title" name="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          <FormInput label="Subtitle" name="subtitle" value={formData.subtitle} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} />
          <FormInput label="Description" name="description" type="textarea" rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormInput label="Button Text" name="buttonText" value={formData.buttonText} onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })} />
            <FormInput label="Button Link" name="buttonLink" value={formData.buttonLink} onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })} />
          </div>
          
          <FormInput label="Background Style" name="backgroundColor" type="select" value={formData.backgroundColor} onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })} options={backgroundOptions} />
          <FormInput label="Order" name="order" type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: e.target.value })} />
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Image</label>
            {imagePreview && (
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', marginBottom: '0.5rem', display: 'block' }} 
              />
            )}
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageChange} 
              style={{ width: '100%', marginBottom: '0.5rem', padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
            />
            <input 
              type="text" 
              value={formData.imageUrl} 
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} 
              placeholder="Image URL" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginTop: '0.5rem' }} 
            />
            <small style={{ color: '#5A6E7F' }}>Upload an image or enter a URL</small>
          </div>
          
          <FormInput label="Active" name="isActive" type="checkbox" value={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
          
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" disabled={loading || uploading} style={{ flex: 1, padding: '0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              {loading || uploading ? 'Saving...' : 'Save Slide'}
            </button>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.75rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HeroForm;